/// <amd-module name="VSSUI/Components/Label/Label" />
import * as React from "react";
import { css, getSafeId } from "VSSUI/Util";
import { ILabelProps } from "./Label.Props";
import { Tooltip } from "VSSUI/TooltipEx";
import { IColor, getColorString, isDark } from "VSSUI/Utilities/Color";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZoneContext, IFocusZoneContext } from "VSSUI/FocusZone";
import { Observer } from "VSSUI/Observer";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { FocusGroupContext } from "VSSUI/FocusGroup";

interface ILabelState {
    isHovered: boolean;
}

export class Label extends React.Component<ILabelProps, ILabelState> {
    public static readonly DEFAULT_COLOR: IColor = { red: 240, blue: 240, green: 240 };
    public static contextType = FocusGroupContext;

    private rootRef = React.createRef<HTMLDivElement>();

    constructor(props: ILabelProps) {
        super(props);

        this.state = {
            isHovered: false
        };
    }

    public render() {
        const { className, content, color, enableHover = false, excludeFocusZone, id, selected, onClick, onKeyDown, onMouseDown } = this.props;
        const { isHovered } = this.state;
        const renderColor = color && isHovered && enableHover ? createHoverColor(color) : color;

        const labelObject = (
            <FocusZoneContext.Consumer>
                {(zoneContext: IFocusZoneContext) => (
                    <FocusWithin onBlur={this.onBlur} onFocus={this.onFocus}>
                        {(focusStatus: IFocusWithinStatus) => {
                            return (
                                <Observer selected={selected}>
                                    {(observerProps: { selected: boolean }) => (
                                        <div
                                            className={css(
                                                className,
                                                "bolt-label",
                                                observerProps.selected && "selected",
                                                color && (this.isDark(color, observerProps.selected) ? "dark" : "light")
                                            )}
                                            data-focuszone={!excludeFocusZone && zoneContext.focuszoneId}
                                            id={getSafeId(id)}
                                            onBlur={this.onBlur}
                                            onClick={onClick}
                                            onFocus={this.onFocus}
                                            onKeyDown={onKeyDown}
                                            onMouseDown={onMouseDown}
                                            onMouseEnter={this.onHoverStart}
                                            onMouseLeave={this.onHoverEnd}
                                            ref={this.rootRef}
                                            style={this.getStyle(renderColor, observerProps.selected, focusStatus.hasFocus)}
                                            tabIndex={getTabIndex(this.props, this.context)}
                                        >
                                            <Tooltip overflowOnly={true}>
                                                <div className="bolt-label-content text-ellipsis">{content}</div>
                                            </Tooltip>
                                        </div>
                                    )}
                                </Observer>
                            );
                        }}
                    </FocusWithin>
                )}
            </FocusZoneContext.Consumer>
        );

        return labelObject;
    }

    public focus() {
        this.rootRef.current && this.rootRef.current.focus();
    }

    private getStyle(color: IColor, isSelected: boolean, hasFocus: boolean) {
        if (isSelected || hasFocus || !color) {
            return undefined;
        }

        return { backgroundColor: getColorString(color) };
    }

    private isDark(color: IColor, isSelected: boolean): boolean {
        if (isSelected || !color) {
            return false;
        }

        return isDark(color);
    }

    private onBlur = () => {
        this.props.onBlur && this.props.onBlur();
    };

    private onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        this.props.onFocus && this.props.onFocus(event);
        this.context && this.props.id && this.context.onFocus(this.props.id);
    };

    private onHoverStart = () => {
        this.setState({
            isHovered: true
        });
    };

    private onHoverEnd = () => {
        this.setState({
            isHovered: false
        });
    };
}

function createHoverColor(baseColor: IColor): IColor {
    const darkenFactor: number = 0.06;
    const darkenMultiplier: number = 1 - darkenFactor;

    return {
        red: baseColor.red * darkenMultiplier,
        green: baseColor.green * darkenMultiplier,
        blue: baseColor.blue * darkenMultiplier
    };
}
